"use client";

import * as React from "react";
import { AnimatePresence, motion } from "motion/react";
import { Check } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { springs, transitions, durations } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Avatar } from "../primitives/Avatar";
import { Spinner } from "../primitives/Spinner";

/**
 * WorkspaceTransitionOverlay: full-bleed blocking overlay for async transitions
 * that must finish before the user moves on (the canonical case: switching
 * workspaces). It runs a small phase machine: it opens on a spinner, morphs to a
 * success check once the in-flight `loading` clears, then auto-dismisses via
 * `onComplete`. A hang fallback fires `onComplete` after `timings.fallback` so a
 * stalled request never traps the user behind the scrim.
 *
 * Presentational only (props in, `onComplete` out). The donor in the unified app
 * was wired to a Redux host; that host stays in the app and maps slice fields to
 * these props. Promoted (and de-app-coupled) from unified's
 * `WorkspaceTransitionToast`, renamed because it is a blocking overlay, not a
 * corner toast (`Toaster`/sonner already owns "toast"). The app glyphs are not
 * imported here: the fallback figure is the `icon` slot, falling through to
 * `Avatar`'s name-initials when neither `avatar` nor `icon` is set.
 *
 * Sibling of `LogoutLoader`: same `fixed inset-0 z-[9999]` AnimatePresence
 * overlay shell and a11y posture (a live region announces the heading swap), with
 * the added phase morph. Composes `Avatar` (the rounded brand tile) and `Spinner`.
 *
 * Motion: scrim cross-fades on `transitions.surface`; the card pops in on
 * `springs.bouncy`; the badge swaps spinner ↔ check under `AnimatePresence
 * mode="wait"`, the success circle scaling 0.6 → 1 on `springs.bouncy`; the copy
 * cross-fades on phase change. Every animation is gated on `useReducedMotion()`:
 * under reduce, motion collapses to opacity-only and the spring/scale are dropped,
 * but the phase TIMINGS are retained so the success state and auto-dismiss stay
 * perceivable.
 *
 * @example
 *   <WorkspaceTransitionOverlay
 *     open={!!transition}
 *     loading={transition.loading}
 *     name={transition.name}
 *     type={transition.type}
 *     avatar={transition.avatar}
 *     icon={transition.type === "personal" ? <PersonalGlyph /> : <TeamGlyph />}
 *     onComplete={() => dispatch(transitionCleared())}
 *   />
 */

type Phase = "loading" | "success";

/** Tunable phase timings (ms). Consumers can override without forking. */
export interface WorkspaceTransitionTimings {
  /** Delay before flipping to the success check once `loading` clears. Debounces an instant resolve so the spinner is always seen for a beat. Default 100. */
  successDelay?: number;
  /** Delay after success before `onComplete` fires (auto-dismiss). Default 1500. */
  dismiss?: number;
  /** Hard safety net: fire `onComplete` even while still loading, so a hung transition never traps the user. Default 10000. */
  fallback?: number;
}

const DEFAULT_TIMINGS = {
  successDelay: 100,
  dismiss: 1500,
  fallback: 10000,
} as const;

/**
 * The exact state machine from unified's WorkspaceTransitionToast, lifted into a
 * hook. SSR-safe: every `setTimeout` runs inside an effect, never at module top.
 *
 *  - opens in `"loading"`.
 *  - `onCompleteRef` keeps the callback fresh so a fired timer never calls a
 *    stale closure.
 *  - resets to `"loading"` (and clears the dismiss guard/timer) whenever the
 *    overlay closes OR `name` changes, so a second switch to a different target
 *    re-animates cleanly instead of flashing the prior success.
 *  - `loading && phase==="loading"`  → arm the hang fallback.
 *  - `!loading && phase==="loading"` → after `successDelay`, flip to success.
 *  - `!loading && phase==="success"` (once, guarded) → after `dismiss`, complete.
 */
function usePhasedTransition(
  open: boolean,
  loading: boolean,
  name: string,
  onComplete: () => void,
  successDelay: number,
  dismiss: number,
  fallback: number,
): Phase {
  const [phase, setPhase] = React.useState<Phase>("loading");

  const onCompleteRef = React.useRef(onComplete);
  const dismissTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasStartedDismissRef = React.useRef(false);
  const nameRef = React.useRef(name);

  React.useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Reset to the loading phase whenever the overlay closes or the target changes.
  React.useEffect(() => {
    if (!open || nameRef.current !== name) {
      setPhase("loading");
      hasStartedDismissRef.current = false;
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      nameRef.current = name;
    }
  }, [open, name]);

  React.useEffect(() => {
    if (!open) return undefined;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let toSuccess: ReturnType<typeof setTimeout> | undefined;
    if (loading && phase === "loading") {
      // Safety net: never trap the user behind the overlay if a switch hangs.
      fallbackTimer = setTimeout(() => onCompleteRef.current(), fallback);
    } else if (!loading && phase === "loading") {
      toSuccess = setTimeout(() => setPhase("success"), successDelay);
    } else if (!loading && phase === "success" && !hasStartedDismissRef.current) {
      hasStartedDismissRef.current = true;
      dismissTimerRef.current = setTimeout(() => onCompleteRef.current(), dismiss);
    }
    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (toSuccess) clearTimeout(toSuccess);
    };
  }, [open, loading, phase, name, successDelay, dismiss, fallback]);

  return phase;
}

const cardVariants = cva(
  "flex flex-col items-center gap-3.5 bg-card border border-border rounded-2xl shadow-lift",
  {
    variants: {
      size: {
        default: "w-[min(340px,calc(100vw-48px))] px-7 py-[30px]",
        sm: "w-[min(300px,calc(100vw-48px))] px-6 py-6",
      },
    },
    defaultVariants: { size: "default" },
  },
);

const scrimVariants = cva(
  "fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-[3px]",
  {
    variants: {
      // Navy is the brand scrim (the donor's rgba(10,37,64,.42) == --primary at
      // 42%). `ink` is a denser variant for surfaces that want a harder block.
      scrim: {
        navy: "bg-[hsl(var(--primary)/0.42)]",
        ink: "bg-[hsl(var(--primary)/0.6)]",
      },
    },
    defaultVariants: { scrim: "navy" },
  },
);

export interface WorkspaceTransitionOverlayProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "title" | "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
  >,
    VariantProps<typeof scrimVariants>,
    VariantProps<typeof cardVariants> {
  /** Controls visibility of the overlay. Matches `LogoutLoader`'s `open`. */
  open: boolean;
  /** Whether the underlying async work is still in flight. The phase machine flips to success once this is false. */
  loading: boolean;
  /** Fired once when the overlay should clear: after the success dwell, or immediately on the hang fallback. The consumer unmounts/closes in response. */
  onComplete: () => void;
  /** Target name. Used in the default copy, as the `Avatar` name (initials fallback), and to re-arm the machine when it changes mid-flight. */
  name: string;
  /** Workspace kind. Drives the default pill copy. Free-form strings are allowed; only `"personal"` is special-cased for copy. */
  type?: "personal" | "team" | string;
  /** Avatar image URL. Wins over `icon` and initials. */
  avatar?: string | null;
  /** Fallback figure shown in the tile when there is no `avatar` (e.g. the app's workspace glyph). Falls through to `name` initials when omitted. */
  icon?: React.ReactNode;
  /** Heading override. Defaults to the workspace copy, switching with phase. */
  title?: React.ReactNode;
  /** Subtext override. Defaults to the workspace copy, switching with phase. */
  description?: React.ReactNode;
  /** Pill text override. Defaults to "Personal Workspace" / "Team Workspace" from `type`. Pass `null` to hide the pill. */
  pillLabel?: React.ReactNode;
  /** Accessible label for the loading badge. Default "Loading". */
  loadingLabel?: string;
  /** Accessible label for the success badge. Default "Done". */
  successLabel?: string;
  /** Mount/unmount + phase animation. Matches `LogoutLoader`. Default true. */
  animate?: boolean;
  /** Override the phase timings (successDelay / dismiss / fallback ms). */
  timings?: WorkspaceTransitionTimings;
}

export const WorkspaceTransitionOverlay = React.forwardRef<
  HTMLDivElement,
  WorkspaceTransitionOverlayProps
>(
  (
    {
      open,
      loading,
      onComplete,
      name,
      type = "team",
      avatar,
      icon,
      title,
      description,
      pillLabel,
      loadingLabel = "Loading",
      successLabel = "Done",
      animate = true,
      timings,
      scrim,
      size,
      className,
      ...props
    },
    ref,
  ) => {
    const successDelay = timings?.successDelay ?? DEFAULT_TIMINGS.successDelay;
    const dismiss = timings?.dismiss ?? DEFAULT_TIMINGS.dismiss;
    const fallback = timings?.fallback ?? DEFAULT_TIMINGS.fallback;
    const phase = usePhasedTransition(
      open,
      loading,
      name,
      onComplete,
      successDelay,
      dismiss,
      fallback,
    );

    const reduced = useReducedMotion();
    // null (unresolved, SSR/first paint) defaults to allowing motion; reduce
    // collapses to opacity-only with no spring/scale, timings retained.
    const motionOn = animate && reduced !== true;

    const isPersonal = type === "personal";
    const heading =
      title ?? (phase === "loading" ? "Switching workspace…" : "Workspace switched");
    const sub =
      description ??
      (phase === "loading" ? `Loading ${name}` : `You're now in ${name}`);
    const pill =
      pillLabel === null
        ? null
        : (pillLabel ?? (isPersonal ? "Personal Workspace" : "Team Workspace"));

    const cardSpring = motionOn ? springs.bouncy : { duration: 0 };

    const content = (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label={typeof heading === "string" ? heading : "Workspace transition"}
        className={cn(scrimVariants({ scrim }), className)}
        {...props}
      >
        <motion.div
          data-slot="card"
          className={cn(cardVariants({ size }))}
          initial={motionOn ? { opacity: 0, scale: 0.94 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={cardSpring}
        >
          {/* Tile + status badge */}
          <div className="relative" data-slot="tile">
            <Avatar
              size="xl"
              shape="rounded"
              src={avatar ?? undefined}
              name={name}
              fallback={icon}
              className="h-[60px] w-[60px] bg-primary text-primary-foreground"
            />
            <span
              data-slot="badge"
              className="absolute -bottom-1.5 -right-1.5 flex h-[26px] w-[26px] items-center justify-center rounded-full bg-card border-2 border-border"
            >
              <AnimatePresence mode="wait" initial={false}>
                {phase === "loading" ? (
                  <motion.span
                    key="loading"
                    initial={motionOn ? { opacity: 0 } : false}
                    animate={{ opacity: 1 }}
                    exit={motionOn ? { opacity: 0 } : undefined}
                    transition={transitions.snappy}
                    className="flex items-center justify-center"
                    role="img"
                    aria-label={loadingLabel}
                  >
                    {/* Decorative: the outer region already announces the
                        phase, so the spinner must not add a second role=status
                        / live announcement. The accessible label lives on the
                        badge wrapper instead. */}
                    <Spinner size="default" className="text-primary" decorative />
                  </motion.span>
                ) : (
                  <motion.span
                    key="success"
                    initial={motionOn ? { opacity: 0, scale: 0.6 } : false}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={motionOn ? { opacity: 0 } : undefined}
                    transition={motionOn ? springs.bouncy : { duration: 0 }}
                    className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-success text-success-foreground"
                    role="img"
                    aria-label={successLabel}
                  >
                    <Check className="h-[13px] w-[13px]" strokeWidth={3} aria-hidden />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </div>

          {/* Copy: cross-fades on phase change */}
          <div className="text-center">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={phase}
                initial={motionOn ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                exit={motionOn ? { opacity: 0 } : undefined}
                transition={{ duration: motionOn ? durations.hover : 0, ease: transitions.snappy.ease }}
              >
                <h3 className="m-0 text-base font-semibold text-foreground">{heading}</h3>
                <p className="mx-0 mb-0 mt-1 text-[13px] text-muted-foreground">{sub}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {pill != null ? (
            <span className="rounded-full border border-border bg-secondary px-2.5 py-[3px] text-[11px] font-semibold uppercase tracking-[0.02em] text-muted-foreground">
              {pill}
            </span>
          ) : null}
        </motion.div>
      </div>
    );

    if (animate) {
      return (
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transitions.surface}
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>
      );
    }

    return open ? content : null;
  },
);
WorkspaceTransitionOverlay.displayName = "WorkspaceTransitionOverlay";
